import React from "react";
import { cn } from "./cn";

export interface SegmentOption<V extends string> {
  value: V;
  label: React.ReactNode;
  /** Contador ao lado do rótulo (ex.: itens já escolhidos naquela aba). */
  count?: number;
}

interface SegmentedControlProps<V extends string> {
  options: SegmentOption<V>[];
  value: V;
  onChange: (value: V) => void;
  "aria-label": string;
  /** soft: fundo claro e escolhido em branco com texto azul (sub-escolhas, ex.: tipo de pacote). */
  tone?: "solid" | "soft";
  className?: string;
}

export function SegmentedControl<V extends string>({ options, value, onChange, tone = "solid", className, ...rest }: SegmentedControlProps<V>) {
  return (
    <div
      role="group"
      aria-label={rest["aria-label"]}
      className={cn("grid auto-cols-fr grid-flow-col gap-1 rounded-button border border-line bg-surface-hover p-1", className)}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              "flex min-h-control-touch items-center justify-center gap-1.5 rounded-button px-3 text-body font-semibold transition-colors duration-base ease-out",
              "focus-visible:outline-none focus-visible:shadow-focus",
              !selected && "text-fg-muted hover:text-fg",
              selected && (tone === "soft" ? "bg-surface text-primary shadow-raised" : "bg-primary text-on-primary shadow-cta")
            )}
          >
            {option.label}
            {option.count !== undefined && (
              <span
                className={cn(
                  "inline-grid h-5 min-w-5 place-items-center rounded-full px-1.5 text-caption font-bold",
                  selected && tone === "solid" ? "bg-on-primary/25 text-on-primary" : "bg-surface text-primary"
                )}
              >
                {option.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
